/**
 * SMS Signup Block
 *
 * Shopify themes cannot call Postscript's private Add Subscriber API
 * (CORS, and the key must not live in Liquid). There is no app proxy
 * on this store. Storefront path: window.postscript.acquisition.optIn.
 * That is Onsite Opt-in. It does not send Reply Y.
 */
class SmsSignupBlock extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLFormElement | null} */
    this.form = null;
    /** @type {HTMLInputElement | null} */
    this.phoneInput = null;
    /** @type {HTMLSelectElement | null} */
    this.countrySelect = null;
    /** @type {HTMLInputElement | null} */
    this.consentInput = null;
    /** @type {HTMLElement | null} */
    this.errorMessage = null;
    /** @type {HTMLElement | null} */
    this.successMessage = null;

    /** @type {HTMLButtonElement | null} */
    this.flagButton = null;
    /** @type {HTMLImageElement | null} */
    this.flagImage = null;
    /** @type {HTMLElement | null} */
    this.flagMenu = null;

    this.onSubmit = this.onSubmit.bind(this);
    this.onCountryChange = this.onCountryChange.bind(this);
    this.onFlagButtonClick = this.onFlagButtonClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onFlagKeydown = this.onFlagKeydown.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('.sms-signup__form');
    if (!this.form) return;

    this.phoneInput = this.form.querySelector('input[name="phone"]');
    this.countrySelect = this.form.querySelector('select[name="country"]');
    this.consentInput = this.form.querySelector('input[name="sms_consent"]');
    this.errorMessage = this.form.querySelector('.email-signup__message--error');
    this.successMessage = this.form.querySelector('.email-signup__message--success');

    this.form.addEventListener('submit', this.onSubmit);
    this.countrySelect?.addEventListener('change', this.onCountryChange);
    this.setupFlagPicker();
    this.onCountryChange();
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmit);
    }
    this.countrySelect?.removeEventListener('change', this.onCountryChange);
    this.flagButton?.removeEventListener('click', this.onFlagButtonClick);
    this.flagButton?.removeEventListener('keydown', this.onFlagKeydown);
    document.removeEventListener('click', this.onDocumentClick);
  }

  /**
   * Windows cannot render emoji flags (shows "US"). PNG flags + names live in Liquid.
   */
  setupFlagPicker() {
    if (!this.form || !this.countrySelect) return;

    this.flagButton = this.form.querySelector('.sms-signup__flag-button');
    this.flagImage = this.flagButton?.querySelector('.sms-signup__flag-img') || null;
    this.flagMenu = this.form.querySelector('.sms-signup__flag-menu');
    if (!this.flagButton || !this.flagMenu) return;

    this.flagMenu.querySelectorAll('.sms-signup__flag-option').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.index);
        if (!this.countrySelect || Number.isNaN(index)) return;
        this.countrySelect.selectedIndex = index;
        this.countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.closeFlagMenu();
        this.flagButton?.focus();
      });
    });

    this.flagButton.addEventListener('click', this.onFlagButtonClick);
    this.flagButton.addEventListener('keydown', this.onFlagKeydown);
    document.addEventListener('click', this.onDocumentClick);
    this.syncFlagButton();
  }

  /**
   * @param {string} iso
   */
  flagUrl(iso) {
    return `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;
  }

  onFlagButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.flagMenu?.hidden) {
      this.openFlagMenu();
    } else {
      this.closeFlagMenu();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  onFlagKeydown(event) {
    if (event.key === 'Escape') {
      this.closeFlagMenu();
    }
    if (event.key === 'ArrowDown' && this.flagMenu?.hidden) {
      event.preventDefault();
      this.openFlagMenu();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  onDocumentClick(event) {
    if (!this.flagMenu || this.flagMenu.hidden) return;
    const target = event.target;
    if (target instanceof Node && this.form?.querySelector('.sms-signup__country')?.contains(target)) {
      return;
    }
    this.closeFlagMenu();
  }

  openFlagMenu() {
    if (!this.flagMenu || !this.flagButton) return;
    this.flagMenu.hidden = false;
    this.flagButton.setAttribute('aria-expanded', 'true');
  }

  closeFlagMenu() {
    if (!this.flagMenu || !this.flagButton) return;
    this.flagMenu.hidden = true;
    this.flagButton.setAttribute('aria-expanded', 'false');
  }

  syncFlagButton() {
    const option = this.countrySelect?.selectedOptions[0];
    const iso = (option?.dataset.iso || 'US').toLowerCase();
    if (this.flagImage) {
      this.flagImage.src = this.flagUrl(iso);
    }
    this.flagMenu?.querySelectorAll('.sms-signup__flag-option').forEach((button) => {
      button.classList.toggle('is-selected', button.dataset.index === String(this.countrySelect?.selectedIndex));
    });
  }

  onCountryChange() {
    if (!this.countrySelect) return;

    const option = this.countrySelect.selectedOptions[0];
    const name = option?.title || option?.textContent?.trim() || '';
    if (name) {
      this.countrySelect.title = name;
      this.flagButton?.setAttribute('aria-label', name);
    }
    this.syncFlagButton();
  }

  /**
   * @param {SubmitEvent} event
   */
  async onSubmit(event) {
    event.preventDefault();
    this.hideMessages();

    if (this.form?.classList.contains('is-success')) return;

    if (!this.consentInput || !this.consentInput.checked) {
      this.showError(this.dataset.errorConsent || 'Please agree to receive SMS messages.');
      this.consentInput?.focus();
      return;
    }

    const phoneNumber = this.buildPhoneNumber();
    if (!phoneNumber) {
      this.showError(this.dataset.errorPhone || 'Enter a valid phone number.');
      this.phoneInput?.focus();
      return;
    }

    this.setSubmitting(true);

    try {
      await this.submitWithSdk(phoneNumber);
      this.showSuccess();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : this.dataset.errorGeneric || 'Unable to subscribe right now.';
      this.showError(message);
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * @returns {((phone: string) => Promise<unknown>) | null}
   */
  getSdkOptIn() {
    const optIn = window.postscript?.acquisition?.optIn;
    return typeof optIn === 'function' ? optIn.bind(window.postscript.acquisition) : null;
  }

  /**
   * @param {number} [timeoutMs]
   * @returns {Promise<Record<string, unknown>>}
   */
  waitForSdk(timeoutMs = 8000) {
    const ready = () => window.postscript && typeof window.postscript === 'object';

    if (ready()) {
      return Promise.resolve(window.postscript);
    }

    return new Promise((resolve, reject) => {
      let settled = false;

      const finish = (ok) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        window.removeEventListener('postscriptReady', onReady);
        if (ok && ready()) {
          resolve(window.postscript);
        } else {
          reject(new Error(this.dataset.errorConfig || 'Postscript is not connected.'));
        }
      };

      const timer = setTimeout(() => finish(false), timeoutMs);
      const onReady = () => finish(true);
      window.addEventListener('postscriptReady', onReady, { once: true });

      if (ready()) finish(true);
    });
  }

  /**
   * @param {string} phoneNumber
   */
  async submitWithSdk(phoneNumber) {
    const sdk = await this.waitForSdk();
    const optIn = this.getSdkOptIn() || this.readFn(sdk, ['acquisition', 'optIn']);

    if (typeof optIn !== 'function') {
      throw new Error(this.dataset.errorConfig || 'Postscript is not connected.');
    }

    const result = await optIn(phoneNumber);

    if (this.isRejected(result)) {
      throw new Error(this.readError(result) || this.dataset.errorGeneric || 'Unable to subscribe right now.');
    }

    return result;
  }

  /**
   * @param {unknown} object
   * @param {string[]} path
   * @returns {Function | null}
   */
  readFn(object, path) {
    let current = object;
    for (const key of path) {
      if (!current || typeof current !== 'object') return null;
      current = current[key];
    }
    return typeof current === 'function' ? current : null;
  }

  /**
   * @param {unknown} result
   */
  isRejected(result) {
    if (result === false) return true;
    if (!result || typeof result !== 'object') return false;

    const data = /** @type {Record<string, unknown>} */ (result);
    if (data.success === false || data.ok === false) return true;
    if (data.error) return true;
    return false;
  }

  /**
   * @returns {string}
   */
  buildPhoneNumber() {
    const raw = this.phoneInput?.value?.trim() || '';
    if (!raw) return '';

    let digits = raw.replace(/\D/g, '');
    if (digits.length < 7) return '';

    if (raw.startsWith('+')) {
      return `+${digits}`;
    }

    const dialCode = (this.countrySelect?.value || '+1').replace(/\s/g, '');
    const dialDigits = dialCode.replace(/\D/g, '');
    if (digits.startsWith(dialDigits) && digits.length > dialDigits.length + 5) {
      return `+${digits}`;
    }

    if (digits.startsWith('0')) {
      digits = digits.replace(/^0+/, '');
    }

    return `${dialCode}${digits}`;
  }

  /**
   * @param {unknown} result
   * @returns {string}
   */
  readError(result) {
    if (!result || typeof result !== 'object') return '';

    const data = /** @type {Record<string, unknown>} */ (result);
    if (typeof data.error === 'string') return data.error;

    if (data.error && typeof data.error === 'object' && 'message' in data.error) {
      const message = /** @type {{ message?: unknown }} */ (data.error).message;
      if (typeof message === 'string') return message;
    }

    if (Array.isArray(data.errors) && typeof data.errors[0] === 'string') {
      return data.errors[0];
    }

    if (typeof data.message === 'string') return data.message;

    return '';
  }

  /**
   * @param {boolean} isSubmitting
   */
  setSubmitting(isSubmitting) {
    this.form?.querySelectorAll('button[type="submit"]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = isSubmitting;
    });
  }

  /**
   * @param {string} message
   */
  showError(message) {
    if (!this.errorMessage) return;

    const text = this.errorMessage.querySelector('.email-signup__message-text');
    if (text) text.textContent = message;

    this.errorMessage.hidden = false;
    this.errorMessage.focus();
  }

  showSuccess() {
    this.form?.classList.add('is-success');
    if (this.phoneInput) this.phoneInput.value = '';
    if (this.successMessage) {
      this.successMessage.hidden = false;
      this.successMessage.focus();
    }
  }

  hideMessages() {
    if (this.errorMessage) this.errorMessage.hidden = true;
    if (this.successMessage) this.successMessage.hidden = true;
  }
}

if (!customElements.get('sms-signup-block')) {
  customElements.define('sms-signup-block', SmsSignupBlock);
}
